import { FilterItemType, PageFiltersType, AreaItemtype, CountryItemType } from '@/pages/dashboard/types/dashboard';
import moment from 'moment';
import { ALL_VALUE, LANGUAGE_CN, LANGUAGE_EN, timeTypeTrans } from '@/pages/dashboard/_config/constant';
import { FILTER_CACHE_VALUE_KEY, storage } from '@/utils/utils';
import { FILTER_VALUE_TYPE_DATE, DEFAULT_FILTER_CONFIG, SELECTOR_SAVE_KEY_MAP, SELECTOR_SAVE_KEY_CODE, FUNNEL_SELECTOR_KEY_MAP, PC_CONSOLE_DEFAULT_FILTER_CONFIG, CASUAL_DEFAULT_FILTER_CONFIG } from './_config';
import { RANGE_DATE, RANGE_DAILY, RANGE_WEEKLY, RANGE_MONTHLY, RANGE_TIME, REGION, DATE_FILTER_ARR, SOURCE, CATEGORY, DATE_RANGE_ARR, PRODUCT, DATE_RANGE_DAILY_WEEK_MONTH_ARR, DATE_SINGLE_ARR, CONTRAST_DAITE, RANGE_DAY_MONTH, DATE_TIME_RANGE_EXCLUDE_CONTRAST, SINGLE_DAILY, DATE_FILTER_ARR_NO_REBASE, BUILTIN_CASCADE_RENDER_KEYS } from '@/pages/dashboard/components/NewFilter/FilterBase/_config';
import {
  FormatOptionsParamsType,
  IBundleSelectorPayload,
  IFilterOptionDisplayOrderConfig,
  InputItemType,
  TFilterOptionSourceRow,
  TFilterSelectorPayload,
  UrlParams,
  filterValueType,
  urlParamsObj,
  urlParamsObjKeys,
} from './_type';
import { changeDateTypeValue, getDateVal, getRangeDateLength } from '../../utils/utils';
import { isEmpty, isEqual, uniqBy } from 'lodash-es';
import { getCountryFlag } from '@home/_config/countries';
import { mapCascadeFields } from '../CascadeFilterComponent/fieldMapper';

export function findPageSelector(pagePath: string, pageSelector: PageFiltersType[], defaultSelector: PageFiltersType[], dataFrame: string, pageDefaultSelector?: FilterItemType[],useFrontFilterConfig?:boolean): FilterItemType[] {
  const pagePathVal = `/${pagePath}`?.trim()?.toLowerCase();
  let findSelector = pageSelector?.find((item: PageFiltersType) => 
    // 路径完全匹配
    pagePathVal === item?.path?.trim()?.toLowerCase()
  );
    // 不能合并到上面pageSelector.find处理，不然当同时配置/overview/monthly和/overview/monthly/breakdown的时候，可能拿到的是/overview/monthly
  findSelector = getFilterSelector(pageSelector, findSelector, pagePathVal);
  if (findSelector) {
    return [...(findSelector?.filters || [])];
  }
  // 查找通配路径
  pageSelector?.filter((item: PageFiltersType) => {
    const { path } = item;
    if(!path.includes('*')) {
      return false;
    }
    const lowerPagePath  = `/${pagePath}`.toLowerCase();
    // 路径通配
    return lowerPagePath?.startsWith(path.replace('*', '')?.toLowerCase()) || lowerPagePath?.startsWith(path.replace('/*', '')?.toLowerCase());
  })?.forEach((item: PageFiltersType) => {
    if (!findSelector) {
      findSelector = item;
    } else if (findSelector?.path?.length < item?.path?.length) {
      findSelector = item;
    }
  });
  if (findSelector) {
    return [...(findSelector?.filters || [])];
  }
  if(useFrontFilterConfig && pageDefaultSelector) {
    return pageDefaultSelector;
  }
  // 查到默认配置
  findSelector = defaultSelector?.find((item: PageFiltersType) => item?.path === 'default');
  if (findSelector) {
    return [...(findSelector?.filters || [])];
  }

  const defaultValObj: any = {
    'default': DEFAULT_FILTER_CONFIG,
    'mobile': DEFAULT_FILTER_CONFIG,
    'pc': PC_CONSOLE_DEFAULT_FILTER_CONFIG,
    'casual': CASUAL_DEFAULT_FILTER_CONFIG
  };
  const defaultVal = defaultValObj?.[dataFrame] || defaultValObj?.default;
  const defaultValResult = pageDefaultSelector ? pageDefaultSelector : defaultVal;
  // 返回默认配置
  return [...defaultValResult];
}

/**
 * @description: 模糊匹配，当页面配置的为三段或多段路由的时候，匹配筛选器配置中的少一段的路由
 * @param {PageFiltersType []} pageSelector 所有页面级筛选器配置信息
 * @param {PageFiltersType | undefined} defaultSelect 页面配置path对应的页面级筛选器配置信息
 * @param {string} pagePathVal 页面配置中的path
 */
export const getFilterSelector = (pageSelector: PageFiltersType[], defaultSelect: PageFiltersType | undefined, pagePathVal: string) => {
  let result = defaultSelect;
  // 有页面级配置则直接取页面级配置，没有的话则取模糊匹配的配置
  if(!result) {
    result = pageSelector?.find((item) => {
      const itemPath = item?.path?.trim()?.toLowerCase()?.replace('*', '')?.replace('/*', '');
      // 如果路径包含三个或更多斜杠，去掉最后一个斜杠后的部分再次匹配
      if (pagePathVal?.split('/').length >= 4) {
        const reducedPath = pagePathVal?.substring(0, pagePathVal?.lastIndexOf('/'));
        return itemPath === reducedPath;
      }
      return false;
    });
  }
  return result;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeBundleOptionRow(row: unknown): TFilterOptionSourceRow {
  const o = isPlainRecord(row) ? row : {};
  return {
    ...o,
    bundle_id: o.bundle_id ?? o.code,
    bundle_name_ch: o.bundle_name_ch ?? o.name_ch,
    bundle_name_en: o.bundle_name_en ?? o.name_en,
  };
}

function getBundleOptionRows(bundleField: unknown): TFilterOptionSourceRow[] {
  if (Array.isArray(bundleField)) {
    return bundleField.map(normalizeBundleOptionRow);
  }
  if (isPlainRecord(bundleField)) {
    const nested = bundleField as IBundleSelectorPayload;
    const list = nested.option_list ?? nested.filter_list ?? [];
    return Array.isArray(list)
      ? list.map(normalizeBundleOptionRow)
      : [];
  }
  return [];
}

/**
 * 从 selector 中解析筛选项的下拉数据源。
 * 优先使用 mapKey 对应字段（如 bundle_list）；对 bundle 做兼容：custom_filters.bundle 可能为数组且项为 code/name_ch/name_en，
 * 需映射为 bundle_id/bundle_name_ch/bundle_name_en 以匹配 renderBundle 的展示字段。
 * @returns 选项数组，无数据时返回 undefined
 */
export function getFilterOptionSource(
  curSelector: TFilterSelectorPayload | null | undefined,
  key: string,
  mapKey: string,
): TFilterOptionSourceRow[] | undefined {
  if (!curSelector) {
    return undefined;
  }
  const rawSource = curSelector[mapKey];
  if (Array.isArray(rawSource)) {
    return rawSource as TFilterOptionSourceRow[];
  }
  if (key === 'bundle' && curSelector.bundle !== undefined && curSelector.bundle !== null) {
    return getBundleOptionRows(curSelector.bundle);
  }
  return undefined;
}

export const formatOptions: (option: FormatOptionsParamsType) => Array<any> = ({ options, valueKey, locale, deleteAllValue = true, cnKey = '', enKey = '', iconKey = '' }) => {
  
  const resultArrMap = deleteAllValue ? ((options || [])?.filter((item: any) => item?.[valueKey] !== ALL_VALUE)) : (options || []);

  const result =  resultArrMap?.map((item: any) => {
    const text = (locale === LANGUAGE_CN ? item?.[cnKey] : item?.[enKey]) || item?.[enKey] || item?.[cnKey];
    return {
      ...item,
      value: item?.[valueKey],
      text,
      icon: item?.[iconKey],
    };
  });
  return result;
};

/**
 * 按配置重排筛选器选项：topMostId → pinnedIds（按表顺序且仅保留数据源存在的项）→ 其余按 sortLabelKey + sortLocale（默认 en）升序。
 * 不改变选项对象字段，仅调整数组顺序；未配置的筛选器请勿调用（调用方判断 config 是否存在）。
 */
export const applyFilterOptionDisplayOrder = <T extends Record<string, any>>(
  options: T[] | undefined,
  config: IFilterOptionDisplayOrderConfig,
): T[] => {
  if (!options?.length || !config?.idKey) {
    return options ? [...options] : [];
  }
  const { idKey, sortLabelKey, pinnedIds = [], topMostId, sortLocale = 'en' } = config || {};
  const idStr = (v: unknown): string => String(v);

  const map = new Map<string, T>();
  options.forEach((item: T) => {
    const id = idStr(item[idKey]);
    if (!map.has(id)) {
      map.set(id, item);
    }
  });

  const used = new Set<string>();
  const result: T[] = [];

  const pushById = (id: string): void => {
    if (used.has(id)) {
      return;
    }
    const item = map.get(id);
    if (item) {
      result.push(item);
      used.add(id);
    }
  };

  if (topMostId !== undefined && topMostId !== null && `${topMostId}` !== '') {
    pushById(idStr(topMostId));
  }
  pinnedIds.forEach(pid => pushById(idStr(pid)));

  const rest = uniqBy(
    options.filter((item: T) => !used.has(idStr(item[idKey]))),
    (item: T) => idStr(item[idKey]),
  );

  rest.sort((a: T, b: T) => {
    const aText = `${a?.[sortLabelKey] ?? ''}`;
    const bText = `${b?.[sortLabelKey] ?? ''}`;
    return aText.localeCompare(bText, sortLocale);
  });
  result.push(...rest);
  return result;
};

// 渠道筛选器，后端返回的数据没有id、icon、中英文，只有值
export const formatNetworkOptions: (option: { options: Array<any>; }) => Array<any> = ({ options }) => (options || [])?.filter((item: any) => item !== ALL_VALUE).map((item: any) => ({
  ...item,
  value: item,
  text: item,
}));

interface FilterCacheValueType {
  gameCode: string;
  value: any;
}


export const getFilterCacheValue: (gameCode: string) => FilterCacheValueType = (gameCode) => {
  const filterCacheValue = storage.getItem(FILTER_CACHE_VALUE_KEY, 'sessionStorage');
  if (!filterCacheValue || filterCacheValue?.gameCode !== gameCode) {
    return {
      gameCode,
      value: undefined
    };
  }
  return filterCacheValue;
};

export const setFilterCacheValue: (value: any, gameCode: string) => void = (value = {}, gameCode) => {
  const { value: oldValue = {} } = getFilterCacheValue(gameCode);
  const valueKeys = Object.keys(value) || [];
  const timeType = valueKeys?.includes('rangeMonthly') ? 'Month' : valueKeys?.includes('rangeWeekly') ? 'Week' : 'Day';
  storage.setItem(FILTER_CACHE_VALUE_KEY, {
    gameCode,
    timeType,
    value: {
      ...oldValue,
      ...value
    }
  }, 'sessionStorage');
};

// 日期筛选器type和继承key转换
export const getRangeDataKey = (type: string): string => {
  let cacheKey = '';
  switch (type) {
    case 'date':
      cacheKey = RANGE_DAILY;
      break;
    case 'week':
      cacheKey = RANGE_WEEKLY;
      break;
    case 'month':
      cacheKey = RANGE_MONTHLY;
      break;
    default:
      break;
  }
  return cacheKey;
};

const changeValueToDate = (value: string | Array<string> | undefined) => {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.map((val) => moment(val));
  }

  return moment(value);
};

export const getRangePickerDefaultValue = (gameCode: string, defaultValue: any): any => {
  const { value: cacheValue } = getFilterCacheValue(gameCode);
  return {
    date: changeValueToDate(cacheValue?.[RANGE_DAILY]) || defaultValue?.date,
    week: changeValueToDate(cacheValue?.[RANGE_WEEKLY]) || defaultValue?.week,
    month: changeValueToDate(cacheValue?.[RANGE_MONTHLY]) || defaultValue?.month,
  };
};

export const formatCacheValue = (inputList: Array<InputItemType>, value: any) => {
  const cacheValue = {} as any;
  inputList.forEach(item => {
    // 获取筛选器类型，优先取type，没type取key值
    if (item?.visible !== 2) {
      const key: string = item.type || item.key;
      if (key === RANGE_DATE) {
        const { type, value: val } = value[item.key];
        const cacheKey = getRangeDataKey(type);
        if (cacheKey) {
          cacheValue[cacheKey] = val;
        }
      } else {
        cacheValue[key] = value[item.key];
      }
    }
  });
  return cacheValue;
};

// export const formatRegion = (value: Array<string> | undefined) => {
//   const marketList = value || [];
//   return {
//     region_type: 'market',
//     markets: marketList,
//     regions: []
//   }
// }

export const getDefaultValue = (inputList: Array<InputItemType>, defaultValue: any, filterSaveDefaultValue: any): any => {
  const renderInputListKey = inputList?.map(item => {
    if (item?.visible === 0) {
      return item.type || item.key;
    }
  })?.filter(item => item) || [];

  const { ext_filter, ...filters } = filterSaveDefaultValue?.filter_cond || {};
  const defaultSaveFilters = {
    ...filters,
    ...ext_filter,
  };
  const isHaveRegion = inputList?.filter(item => item.key === 'region')?.length > 0;
  const isHaveCategory = inputList?.filter(item => item.key === 'category')?.length > 0;
  // 收集所有新级联筛选器的 key
  // 注意：BUILTIN_CASCADE_RENDER_KEYS 中的筛选器在 FilterList.tsx 的内置 render 中自己处理 cascade 格式转换，不在此处转换
  const newCascadeFilterKeys: string[] = [];
  inputList.forEach((item: any) => {
    const key = item?.type || item?.key;
    if (isNewCascadeFilter(item, key) && !BUILTIN_CASCADE_RENDER_KEYS.includes(key)) {
      newCascadeFilterKeys.push(key);
    }
  });
  const hasNewCascadeFilters = newCascadeFilterKeys.length > 0;
  
  // 只有当存在需要特殊处理的筛选器时，才进入特殊处理分支
  // 需要特殊处理的筛选器：region、category 和新的级联筛选器（需要将数组格式转换为对象格式）
  const needSpecialHandling = isHaveRegion || isHaveCategory || hasNewCascadeFilters;
  const filterSaveDefault = !needSpecialHandling ? defaultSaveFilters : {
    ...(defaultSaveFilters || {}),
    // 可能不存在默认筛选器方案
    // 只有当存在 region 筛选器时才处理 region
    ...(isHaveRegion && {
      region: defaultSaveFilters?.region ? {
        markets: defaultSaveFilters?.region,
        region_type: 'market',
        regions: []
      } : defaultValue?.region,
    }),
    // 只有当存在 category 筛选器时才处理 category
    ...(isHaveCategory && {
      category: defaultSaveFilters?.category ? {
        product: defaultSaveFilters?.category,
        category_type: 'product',
        category: []
      } : defaultValue?.category,
    }),
    // 处理新的级联筛选器：将保存方案中的数组格式转换为对象格式
    ...(hasNewCascadeFilters && newCascadeFilterKeys.reduce((acc: any, key: string) => {
      const savedVal = defaultSaveFilters?.[key];
      if (savedVal) {
        // 找到对应的筛选器配置，获取 parentKey 和 childKey
        const filterItem = inputList.find((item: any) => (item?.type || item?.key) === key);
        if (filterItem) {
          const parentKey = filterItem?.parentKey || 'parent';
          const childKey = filterItem?.childKey || key;
          // 如果保存方案中的值是数组格式，转换为对象格式
          if (Array.isArray(savedVal)) {
            acc[key] = {
              [parentKey]: [],
              [childKey]: savedVal,
              type: childKey,
            };
          } else if (savedVal && typeof savedVal === 'object' && !Array.isArray(savedVal)) {
            // 如果已经是对象格式，确保结构正确,实际存储的是数组格式
            acc[key] = {
              [parentKey]: savedVal[parentKey] || [],
              [childKey]: savedVal[childKey] || [],
              type: savedVal?.type || childKey,
            };
          } else {
            // 其他情况，使用 defaultValue 中的值
            acc[key] = defaultValue?.[key];
          }
        }
      } else {
        acc[key] = defaultValue?.[key];
      }
      return acc;
    }, {}))
  };

  const mergeVal = { ...(defaultValue || {}), region: filterSaveDefault?.region };
  inputList.forEach((item:any) => {
    const key: string = item?.type || item?.key;

    /**
     * 新的级联筛选器默认值处理（view_type === 'cascade' | 'singleCascade'，且排除 region / category 及 BUILTIN_CASCADE_RENDER_KEYS）
     * 参照 region 筛选器的处理方式，将默认值包装成级联格式：{ parent: [...], [key]: [...], type: key }
     * 注意：BUILTIN_CASCADE_RENDER_KEYS 中的筛选器在 FilterList.tsx 的内置 render 中自己处理 cascade 格式转换
     */
    if (isNewCascadeFilter(item, key) && !BUILTIN_CASCADE_RENDER_KEYS.includes(key)) {
      const parentKey = item?.parentKey || 'parent';
      // 对于新的级联，childKey 默认使用筛选器 key（如 ua_network）
      const childKey = item?.childKey || key;

      // 1. 优先使用保存方案中的值（如果存在且为对象）
      const savedVal = filterSaveDefault?.[key] || {};
      if (savedVal && typeof savedVal === 'object' && !Array.isArray(savedVal)) {
        mergeVal[key] = {
          [parentKey]: savedVal?.[parentKey] || [],
          [childKey]: savedVal?.[childKey] || [],
          type: savedVal?.type || childKey,
        };
        return;
      }

      // 2. 然后看 defaultValueRef / item.default 中的默认值
      // defaultValue 是在 NewFilter/index.tsx 中通过 defaultValueRef.current 写入的
      const rawDefault = defaultValue?.[key] !== undefined ? defaultValue[key] : item.default;

      // 情况 2.1：默认值已是对象结构（为未来后端直接下发结构预留）
      if (rawDefault && typeof rawDefault === 'object' && !Array.isArray(rawDefault)) {
        mergeVal[key] = {
          [parentKey]: rawDefault[parentKey] || [],
          [childKey]: rawDefault[childKey] || [],
          type: rawDefault.type || childKey,
        };
        return;
      }

      // 情况 2.2：默认值是数组（例如 ['255']），直接当作子级数组
      if (Array.isArray(rawDefault)) {
        mergeVal[key] = {
          [parentKey]: [],
          [childKey]: rawDefault,
          type: childKey,
        };
        return;
      }

      // 情况 2.3：defaultValue 中有非对象 / 非数组的值，直接透传
      if (defaultValue?.[key]) {
        mergeVal[key] = defaultValue[key];
        return;
      }
    }

    // 其他筛选器的处理逻辑（保持原有行为）
    if (filterSaveDefault?.[key]?.length > 0) {
      mergeVal[key] = filterSaveDefault?.[key];
    }
    if ((!mergeVal?.[item?.type] && !mergeVal?.[item?.key]) || renderInputListKey?.includes(key)) {
      mergeVal[key] = defaultValue[key];
    }
  });
  return mergeVal;
};

export const getDefaultValueFromCache = (inputList: Array<InputItemType>, cacheValue: any, defaultValue: any, selectorOptionList:any): any => {
  const mergeVal = {} as any;
  const renderInputListKey = inputList?.map(item => {
    if (item?.visible === 0) {
      return item.type || item.key;
    }
  })?.filter(item => item) || [];
  inputList.forEach(item => {
    // 获取筛选器类型，优先取type，没type取key值
    const key: string = item.type || item.key;
    // 锁定状态，数据格式转换
    if (FILTER_VALUE_TYPE_DATE.includes(key)) { // daily, weekly, monthly
      mergeVal[item.key] = changeValueToDate(cacheValue[key]);
    } else if (key === RANGE_DATE || key === CONTRAST_DAITE) { // 日，周，月等
      const { value } = defaultValue[item.key] || {};
      // TODO 缓存存储timeType 暂时从缓存取区别不同粒度时间继承（从cacheValue外部传入的lockFilterInfo区分??）
      const filterCacheValue = storage.getItem(FILTER_CACHE_VALUE_KEY, 'sessionStorage');
      const timeType = filterCacheValue?.timeType || 'Day';
      const cacheKey = timeType === 'Month' ? 'rangeMonthly' : timeType === 'Week' ? 'rangeWeekly' : 'rangeDaily';
      const typeValue = timeType?.toLowerCase() === 'day' ? 'date' : timeType?.toLowerCase();

      const contrastValue = getDurationContrastDate({ type: typeValue, value: changeValueToDate(cacheValue[cacheKey]) });
      const typeList = item?.typeList || [];
      // churn/allUsers  页面只有week和month所以这里加上includes的判断
      if (cacheKey && typeList?.includes(typeValue)) {
        mergeVal[item.key] = key === CONTRAST_DAITE ? contrastValue : {
          type: typeValue,
          value: changeValueToDate(cacheValue[cacheKey]) || value
        };
      } else {
        mergeVal[item.key] = defaultValue[item.key];
      }
    } else if (DATE_SINGLE_ARR?.includes(key)) {
      // 对比日等
      mergeVal[item.key] = cacheValue[key] || defaultValue[item.key];
    } else {
      const resultVal = getfilterOptionsValue(item, selectorOptionList, cacheValue, defaultValue);
      // 筛选器方案中筛选器值为空则取默认值
      mergeVal[item.key] = resultVal;
      if (key === SOURCE) {
        if (Array.isArray(mergeVal[item.key])) {
          mergeVal[item.key] = mergeVal[item.key][0];
        }
        // mergeVal[item.key] = mergeVal[item.key][0]
      }

      if (key === REGION) {
        mergeVal[item.key] = {
          markets: resultVal?.markets ? resultVal?.markets : resultVal,
          region_type: "market",
          regions: []
        };
      }

      // 产品类型级联筛选器特殊处理
      if (key === CATEGORY) {
        const categoryValueList = cacheValue?.category?.category ? cacheValue?.category?.category : cacheValue?.category;
        const categoryValue = selectorOptionList?.category_list?.map(item => item?.category_code)?.filter(item => categoryValueList?.includes(item));
        const productValueList = cacheValue?.category?.product ? cacheValue?.category?.product : cacheValue?.product;
        const filterProductList: any = [];
        if (categoryValueList?.includes('255')) {
          // 当全选单一产品类型的产品时，移除掉 '255'
          selectorOptionList?.product_list?.map((product: any) => {
            if (product?.product_code !== '255') {
              filterProductList?.push(product?.product_code);
            }
          });
        } else {
          selectorOptionList?.category_list?.filter(item => categoryValueList?.includes(item?.category_code))?.map((category: any) => category?.product_set?.map((product: any) => {
            filterProductList?.push(product?.product_code);
          }));
        }

        let productValue = filterProductList?.filter(item => productValueList?.includes(item));
        // 当没有任何选择时，默认全选255
        if (!productValue || productValue.length === 0) {
          productValue = ['255'];
        }
        mergeVal[item.key] = {
          product: productValue?.length > 0 ? productValue : resultVal?.product,
          category_type: "product",
          category: categoryValue?.length > 0 ? categoryValue : resultVal?.category,
        };
      }
      
      // 新的级联筛选器特殊处理（参照 REGION 的处理方式，不包括 REGION、CATEGORY 及 BUILTIN_CASCADE_RENDER_KEYS）
      // 将 resultVal（数组）包装成级联格式：{ parent: [...], [key]: [...], type: key }
      // 注意：BUILTIN_CASCADE_RENDER_KEYS 中的筛选器在 FilterList.tsx 的内置 render 中自己处理 cascade 格式转换
      if (isNewCascadeFilter(item, key) && !BUILTIN_CASCADE_RENDER_KEYS.includes(key)) {
        const originalValue = cacheValue[key] || defaultValue[key];
        // resultVal 是数组（从 getfilterOptionsValue 返回），直接使用
        // 如果 resultVal 是对象（异常情况），尝试提取 [key] 属性，否则使用空数组
        const childValues = Array.isArray(resultVal) ? resultVal : (resultVal?.[key] || []);
        mergeVal[item.key] = {
          parent: (originalValue && typeof originalValue === 'object' && !Array.isArray(originalValue)) 
            ? (originalValue.parent || []) 
            : [],
          [key]: childValues,
          type: key,
        };
      }
    }
    if (!mergeVal[item.key] || renderInputListKey?.includes(key)) {
      mergeVal[item.key] = defaultValue[item.key];
    }
  });
  
  return mergeVal;
};

const getfilterOptionsValue = (items, selectorOptionList, extendValue, defaultValue) => {
  const key = items?.type || items?.key;
  // 自定义的筛选器，直接取后端传参的key为objKey， 取code 为valueKey
  const objKey = SELECTOR_SAVE_KEY_MAP?.[key] || key;
  const valueKey = SELECTOR_SAVE_KEY_CODE?.[key] || 'code';
  // ua的网络类型筛选器下拉列表是只有值的，所以这里为item?.[valueKey] || item
  const obgData = selectorOptionList?.[objKey]?.map(item => item?.[valueKey] || item) || [];
  let filterItemValue = extendValue?.[key] || defaultValue?.[key];
  
  // 参照 REGION 和 CATEGORY 的处理方式
  if (key === REGION) {
    // 筛选器方案改变地区的时候数据一个数组，手动选择地区时数据是包含market的对象
    filterItemValue = filterItemValue?.markets ? filterItemValue?.markets : filterItemValue;
  }
  if (key === CATEGORY) {
    filterItemValue = filterItemValue?.product ? filterItemValue?.product : filterItemValue;
  }
  /**
   * 新的级联筛选器：提取子值（childKey 就是筛选器的 key）
   * 级联筛选器的值格式：{ parent: [...], [key]: [...], type: parentKey | childKey }
   * 需要提取 [key] 属性的值（数组）用于后续过滤
   */
  if (isNewCascadeFilter(items, key)) {
    filterItemValue = filterItemValue?.[key] ? filterItemValue[key] : filterItemValue;
    const hasAllValue = Array.isArray(filterItemValue) && filterItemValue.includes(ALL_VALUE);
    if (hasAllValue) {
      return [ALL_VALUE];
    }
  }
  // 过滤方案中不存在的选项
  // 确保 filterItemValue 是数组，防止类型错误（如果 filterItemValue 不是数组，includes 会报错）
  let resultVal: any[] = [];
  if (Array.isArray(filterItemValue)) {
    resultVal = obgData?.filter((item: any) => filterItemValue.includes(item)) || [];
  }
  // 如果过滤后没有结果，使用默认值
  resultVal = resultVal?.length === 0 ? defaultValue?.[items.key] : resultVal;
  return resultVal;
};


export const getDefaultValueFromUrl = (inputList: Array<InputItemType>, urlFilterVal: filterValueType, filterParams: any): any => {
  const mergeVal = {} as any;
  const urlFilterParams: any = urlFilterVal?.filterValue || {};
  const renderInputListKey = inputList?.map(item => {
    if (item?.visible === 0) {
      return item.type || item.key;
    }
  })?.filter(item => item) || [];

  inputList?.forEach(item => {
    // 获取筛选器类型，优先取type，没type取key值
    const key: string = item.type || item.key;
    // 锁定状态，数据格式转换
    if (FILTER_VALUE_TYPE_DATE.includes(key) && key === urlFilterVal?.rangeDateKey) { // daily, weekly, monthly
      mergeVal[item.key] = changeValueToDate(urlFilterParams?.[key] || urlFilterParams?.rangeTime);
    } else if (key === RANGE_DATE) { // 日，周，月等
      const { type, value } = filterParams[item.key] || {};
      const cacheKey = getRangeDataKey(type);
      const timeType = urlFilterVal?.timeType;
      // 如果上一个页面和本页面的时间筛选器粒度相同，则对urlFilterParams进行赋值操作
      // 因为urlFilterParams中的key值是固定的rangeTime，没有以getRangeDataKey(type)为key的值
      let tempType = type;
      if (type === 'date') {
        tempType = 'day'; // type='date'特殊处理
      }
      if (tempType === timeType?.toLowerCase()) {
        urlFilterParams[cacheKey] = urlFilterParams?.[RANGE_TIME];
      }
      const typeValue = timeType?.toLowerCase() === 'day' ? 'date' : timeType?.toLowerCase();
      const typeList = item?.typeList || [];
      // 下钻继承后如果没有对应时间粒度则不继承
      if (cacheKey && typeList?.includes(typeValue)) {
        mergeVal[item.key] = {
          type: typeValue,
          value: changeValueToDate(urlFilterParams?.[item.key]) || value
        };
      }
    } else {
      mergeVal[item.key] = urlFilterParams?.[key];
    }
    if (!mergeVal[item.key] || renderInputListKey?.includes(key)) {
      mergeVal[item.key] = filterParams?.[item.key];
    }
  });
  return mergeVal;
};

// 处理保存后端筛选器方案key值转换成formVale驼峰命名key值
export const handleOutFilterKey = (filtrs: { [key: string]: any; }) => {
  const newFilters: { [key: string]: any; } = {};
  Object.keys(filtrs)?.map((key: string) => {
    // 格式化时间
    if (DATE_FILTER_ARR?.includes(key)) {
      if (DATE_RANGE_DAILY_WEEK_MONTH_ARR?.includes(key)) {
        const [start, end] = filtrs[key] || [];
        newFilters[key] = [moment(start), moment(end)];
      } else if (DATE_SINGLE_ARR?.includes(key)) {
        newFilters[key] = moment(filtrs[key]);
      } else {
        const [start, end] = filtrs[key]?.value || [];
        newFilters[key] = {
          ...(filtrs[key] || {}),
          value: [moment(start), moment(end)],
        };
      }
    } else {
      if (FUNNEL_SELECTOR_KEY_MAP?.[key]) {
        newFilters[FUNNEL_SELECTOR_KEY_MAP?.[key]] = filtrs?.[key];
      } else {
        newFilters[key] = filtrs?.[key];
      }
    }
  });
  return newFilters;
};

// 用于格式化存储到本地的筛选器方案，主要用于处理时间类的筛选器的值
// 因为moment对象存入缓存会自动解析，现在存储之前转为时间字符串
export const getFilterSaveDateVal = (saveFilters: { [key: string]: any; }) => {
  const filters = saveFilters?.filter_cond;
  const extFilter = filters?.ext_filter || {};
  const newExtFilters: { [key: string]: any; } = {};
  Object.keys(extFilter)?.map((key: string) => {
    if (DATE_FILTER_ARR?.includes(key)) {
      if (DATE_RANGE_DAILY_WEEK_MONTH_ARR?.includes(key)) {
        const [start, end] = extFilter[key] || [];
        newExtFilters[key] = [changeDateTypeValue(start), changeDateTypeValue(end)];
      } else if (DATE_SINGLE_ARR?.includes(key)) {
        newExtFilters[key] = changeDateTypeValue(extFilter[key]);
      } else if (DATE_FILTER_ARR_NO_REBASE?.includes(key)) {
        newExtFilters[key] = extFilter[key];
      } else {
        const [start, end] = extFilter[key]?.value || [];
        newExtFilters[key] = {
          ...(extFilter[key] || {}),
          value: [changeDateTypeValue(start), changeDateTypeValue(end)],
        };
      }
    } else {
      newExtFilters[key] = extFilter[key];
    }
  });
  return {
    ...(saveFilters || {}),
    filter_cond: {
      ...filters,
      ext_filter: newExtFilters
    }
  };
};

export const handleDateFormat = (extFilter: { [key: string]: Array<string>; }) => {
  const newFilters: { [key: string]: any; } = {};
  Object.keys(extFilter || {})?.map((key: string) => {
    if (DATE_FILTER_ARR?.includes(key)) {
      if (DATE_RANGE_DAILY_WEEK_MONTH_ARR?.includes(key)) {
        const [start, end] = extFilter[key] || [];
        newFilters[key] = [moment(start), moment(end)];
      } else if (DATE_SINGLE_ARR?.includes(key)) {
        newFilters[key] = moment(extFilter[key]);
      } else {
        const [start, end] = extFilter[key]?.value || [];
        newFilters[key] = {
          ...(extFilter[key] || {}),
          value: [moment(start), moment(end)],
        };
      }
    } else {
      newFilters[key] = extFilter?.[key];
    }
  });
  return newFilters;
};


export const getDefaultValueFromSaved = (inputList: Array<InputItemType>, savedValue: any, defaultValue: any, selectorOptionList: any, filterDurationData: any, formValue?: any): any => {
  const mergeVal = {} as any;
  let newSavedValue = {} as any;
  const { ext_filter, ...filters } = savedValue || {};
  if (JSON.stringify(ext_filter) !== '{}' && ext_filter) {
    // 定制页面筛选器值存放在ext_filter
    const extFilter = handleOutFilterKey(ext_filter || {}) || {};
    newSavedValue = { ...filters, ...extFilter };
  } else {
    newSavedValue = filters;
  }
  // review
  // 方案有language，页面不展示language但是有默认值，使用方案时则向后端传参取lanugage默认值
  // 方案有language，页面不展示language也没有默认值，使用方案时则不向后端传参 1
  // 方案没有language，页面展示language，使用方案时传页面的language默认值
  // 方案没有language，页面不展示language但是有languag默认值e，使用方案时传页面的language默认值
  // 进入页面的时候
  const renderInputListKey = inputList?.map(item => {
    if (item?.visible === 0) {
      return item.type || item.key;
    }
  })?.filter(item => item) || [];
  inputList?.forEach(item => {
    // 获取筛选器类型，优先取type，没type取key值(漏斗筛选器驼峰命名key映射得到接口key)
    const key: string = item.type || item.key;
    // 锁定状态，数据格式转换
    if (DATE_FILTER_ARR.includes(key)) { // 所有时间类型排除
      if (!isEmpty(ext_filter)) {
        // 时间相关筛选器都在扩展字段ext_filter中
        // todo 按理说都在savedValue中或者savedValue?.ext_filter中，测试change事件触发的时候是在savedValue中
        const savedVal = { ...(savedValue || {}), ...(savedValue?.ext_filter || {}) };
        if (savedValue?.ext_filter?.dateSaveType === 'dateDuration') {
          mergeVal[item?.key] = getDateDuration(item, savedValue?.ext_filter?.dateSaveVal, filterDurationData) || formValue?.[item?.key] || defaultValue[item.key];
        } else {
          mergeVal[item?.key] = getDateSaveValue(item, savedVal, formValue || defaultValue) || formValue?.[item?.key] || defaultValue[item.key];
        }
      } else {
        // 没有使用筛选器方案的时候
        mergeVal[item?.key] = formValue?.[item?.key] || defaultValue[item.key];
      }

    } else {
      const resultVal = getfilterOptionsValue(item, selectorOptionList, newSavedValue, defaultValue);
      // 筛选器方案中筛选器值为空则取默认值
      mergeVal[item.key] = resultVal;
      if (key === SOURCE) {
        if (Array.isArray(mergeVal[item.key])) {
          mergeVal[item.key] = mergeVal[item.key][0];
        }
        // mergeVal[item.key] = mergeVal[item.key][0]
      }

      if (key === REGION) {
        mergeVal[item.key] = {
          markets: resultVal?.markets ? resultVal?.markets : resultVal,
          region_type: "market",
          regions: []
        };
      }

      // 产品类型级联筛选器特殊处理
      if (key === CATEGORY) {
        const productValueList = newSavedValue?.category?.product ? newSavedValue?.category?.product : newSavedValue?.product;
        const categoryValueList = newSavedValue?.category?.category ? newSavedValue?.category?.category : newSavedValue?.category;
        const filterProductList: any = [];
        if (productValueList?.includes('255')) {
          // 当全选单一产品类型的产品时，移除'255'
          selectorOptionList?.product_list?.map((product: any) => {
            if (product?.product_code !== '255') {
              filterProductList?.push(product?.product_code);
            }
          });
        } else {
          selectorOptionList?.category_list?.filter(item => categoryValueList?.includes(item?.category_code))?.map((category: any) => category?.product_set?.map((product: any) => {
            filterProductList?.push(product?.product_code);
          }));
        }
        let productValue = filterProductList?.filter(item => productValueList?.includes(item));
        // 当没有任何选择时，产品默认全选255
        if (!productValue || productValue.length === 0) {
          productValue = ['255'];
        }
        const categoryValue = selectorOptionList?.category_list?.filter(category => 
          // Check if any product in category.product_set is present in productValue
          category?.product_set?.some(product => productValue.includes(product?.product_code))
        ).map(category => category?.category_code);

        mergeVal[item.key] = {
          product: productValue?.length > 0 ? productValue : resultVal?.product,
          category_type: "product",
          category: categoryValue?.length > 0 ? categoryValue : resultVal?.category,
        };
      }
      
      // 新的级联筛选器特殊处理（参照 REGION 的处理方式，不包括 REGION、CATEGORY 及 BUILTIN_CASCADE_RENDER_KEYS）
      // 将 resultVal（数组）包装成级联格式：{ parent: [...], [key]: [...], type: key }
      // 注意：BUILTIN_CASCADE_RENDER_KEYS 中的筛选器在 FilterList.tsx 的内置 render 中自己处理 cascade 格式转换
      if (isNewCascadeFilter(item, key) && !BUILTIN_CASCADE_RENDER_KEYS.includes(key)) {
        const originalValue = newSavedValue[key] || defaultValue[key];
        // resultVal 是数组（从 getfilterOptionsValue 返回），直接使用
        // 如果 resultVal 是对象（异常情况），尝试提取 [key] 属性，否则使用空数组
        const childValues = Array.isArray(resultVal) ? resultVal : (resultVal?.[key] || []);
        mergeVal[item.key] = {
          parent: (originalValue && typeof originalValue === 'object' && !Array.isArray(originalValue)) 
            ? (originalValue.parent || []) 
            : [],
          [key]: childValues,
          type: key,
        };
      }
    }
    // 筛选器方案中，筛选器不存在则采用默认值
    // 筛选器不存在，则不传参出去,场景：language筛选器
    // 保存的时候，如果筛选器不展示，则取页面默认值
    if (!mergeVal[item.key] || renderInputListKey?.includes(item.key)) {
      mergeVal[item.key] = defaultValue[item.key];
    }
  });
  return mergeVal;
};

// 注意：同粒度继承
export const getDateSaveValue = (item, savedValue, defaultValue) => {
  const key = item?.type || item?.key;
  if (FILTER_VALUE_TYPE_DATE.includes(key)) { // daily, weekly, monthly
    return changeValueToDate(savedValue[key]);
  } if (key === RANGE_DATE || key === CONTRAST_DAITE) { // 日，周，月等
    // || key===CONTRAST_DAITE
    const { value } = defaultValue[item?.key] || {};
    const timeType = savedValue?.timeType || 'Day';
    const saveKey = timeType === 'Month' ? 'rangeMonthly' : timeType === 'Week' ? 'rangeWeekly' : 'rangeDaily';
    const typeValue = timeType?.toLowerCase() === 'day' ? 'date' : timeType?.toLowerCase();

    const contrastValue = getDurationContrastDate({ type: typeValue, value: changeValueToDate(savedValue[saveKey]) });
    const typeList = item?.typeList || [];
    if (saveKey && typeList?.includes(typeValue)) {
      // 时间对比，对比时间特殊处理，页面中处理不了更改筛选器方案时对应的时间变动
      return key === CONTRAST_DAITE ? contrastValue : {
        type: typeValue,
        value: changeValueToDate(savedValue[saveKey]) || value
      };
    } 
    return defaultValue?.[item?.key];
    
  } if (DATE_SINGLE_ARR?.includes(key)) {
    // 对比日等
    return getDateVal(savedValue[key]);
  } if (key === RANGE_DAY_MONTH) {
    // 竞品lanch筛选器
    return savedValue[key];
  } 
  return defaultValue?.[item?.key];
  
};

export const getDateDuration = (item: any, durationType: string, filterDurationData: any) => {
  const key: string = item?.type || item?.key;
  // durationType 中，组件的key和date、week、month为保存方案时duration选项的默认值，其他为快捷选项的key值
  const dateType = filterDurationData?.[durationType]?.type;
  const typeList = item?.typeList || [];
  switch (true) {
    // 当key为singleDaily、contrastDaily时，直接返回null，取页面默认值，因为这两个筛选器没有快捷选项
    case (durationType === SINGLE_DAILY || DATE_SINGLE_ARR?.includes(key)):
      return null;
    case (DATE_TIME_RANGE_EXCLUDE_CONTRAST?.includes(key) && typeList?.includes(dateType)):
      return filterDurationData?.[durationType];
    case key === CONTRAST_DAITE:
      return getDurationContrastDate(filterDurationData?.[durationType]);
    case key === RANGE_DAILY:
    case key === RANGE_WEEKLY:
    case key === RANGE_MONTHLY:
      // eslint-disable-next-line no-case-declarations
      const keyValObj: { [key: string]: string; } = {
        [RANGE_DAILY]: 'date',
        [RANGE_WEEKLY]: 'week',
        [RANGE_MONTHLY]: 'month'
      };
      return dateType === keyValObj?.[key] ? filterDurationData?.[durationType].value : null;
    default:
      return null;
  }
};
const getDurationContrastDate = (durationDate: any) => {
  const { value, type } = durationDate || {};
  const [start, end] = value || [];
  const len = getRangeDateLength(start, end, timeTypeTrans?.[type]);
  const contrastStartDate = {
    date: start?.clone()?.subtract(1, 'day'),
    week: start?.clone()?.subtract(1, 'week'),
    month: start?.clone()?.subtract(1, 'month'),
  };
  const dateMap: any = {
    date: [contrastStartDate?.date, contrastStartDate?.date?.clone()?.add(len, 'day')],
    week: [contrastStartDate?.week, contrastStartDate?.week?.clone()?.add(len, 'week').endOf('week')],
    month: [contrastStartDate?.month, contrastStartDate?.month?.clone()?.add(len, 'month').endOf('month')],
  };
  return {
    type,
    value: dateMap?.[type]
  };
};

export const getRegionSelectedData = (options: any, value: any) => {
  const region: string[] = [];
  const markets = value?.markets;
  let optionsMarketLength = 0;
  options?.map((item: { children: any; country: any; value: string; }) => {
    const optionItemMarkets = item?.children || item?.country;
    const itemArr = [];
    for (let i = 0;i < optionItemMarkets?.length;i++) {
      //  const  marketsValue = markets?.filter (items => items === optionItemMarkets[i]?.value)
      const optionsMarketValue = optionItemMarkets[i]?.value;
      for (let j = 0;j < markets?.length;j++) {
        const marketItem = markets[j];
        if (marketItem === optionsMarketValue) {
          itemArr?.push(marketItem);
        }
      }
      if (itemArr?.length == optionItemMarkets?.length) {
        optionsMarketLength += optionItemMarkets?.length;
        region?.push(item?.value);
      }
    }

  });

  return {
    ...value,
    type: markets?.length === optionsMarketLength ? 'region' : 'market',
    regions: region
  };
};

interface FormatCountryItemType extends CountryItemType {
  chText: string;
  enText: string;
  country_name_abbre: string;
}

interface FormatAreaItemType {
  region_abbre: string;
  region_key: string;
  region_name_cn: string;
  region_name_en: string;
  country: FormatCountryItemType[];
}

// 跟据产品走查提的优化，将地区数据中所有全角改为半角
const changeParentheses = (str: string): string => {
  let result = str?.replace(/（/g, "(");
  result = result?.replace(/）/g, ")");
  return result || '';
};

// 级联筛选器选项格式化：将扁平数组按 type 分组转换为级联结构
export const formatCascadeOptions = (
  options: any[],
  typeKey = 'type',
  valueKey = 'code',
  cnKey = 'name_ch',
  enKey = 'name_en',
  displayOrder?: IFilterOptionDisplayOrderConfig,
): any[] => {
  if (!options || options.length === 0) return [];
  
  // 如果选项已经是级联格式（有 children 属性），直接返回
  if (options[0]?.children || options[0]?.country) {
    return options;
  }
  
  // 按 type 分组
  const groupedByType: { [key: string]: any[]; } = {};
  options.forEach((item: any) => {
    const type = item[typeKey] || 'Others';
    if (!groupedByType[type]) {
      groupedByType[type] = [];
    }
    groupedByType[type].push({
      ...item,
      value: item[valueKey] || item.code,
      text: item[enKey] || item.name_en,
      textCN: item[cnKey] || item.name_ch,
    });
  });
  
  // 转换为级联格式
  // 对 type 进行排序，确保顺序一致
  const sortedTypes = Object.keys(groupedByType)?.sort();
  
  const orderCfg = displayOrder ? {
    ...displayOrder,
    idKey: displayOrder?.idKey || valueKey,
    sortLabelKey: displayOrder?.sortLabelKey || enKey,
  } : undefined;

  return sortedTypes?.map((type) => {
    let sortedChildren = groupedByType[type];
    if (orderCfg) {
      // 每个 type 分组内：topMostId → pinnedIds 中落在本组的项优先，其余按 sortLabelKey + sortLocale
      sortedChildren = applyFilterOptionDisplayOrder(sortedChildren, orderCfg);
    } else {
      sortedChildren = [...groupedByType[type]].sort((a, b) => {
        const aText = `${a?.text ?? ''}`;
        const bText = `${b?.text ?? ''}`;
        return aText.localeCompare(bText, 'en');
      });
    }
    
    return {
      value: type,
      text: type,
      textCN: type,
      children: sortedChildren,
    };
  });
};

// region option 格式化
export const formatRegionOptions: (options: AreaItemtype[]) => Array<FormatAreaItemType> = (options) => (options || []).map(item => {
  // eslint-disable-next-line prefer-const
  let { country_set: country, area_code: areaCode = '', area_name_ch: chText, area_name_en: enText } = item;

  // "China（Hongkong,Macao,Taiwan)"太长放不下，按照产品要求，改为China(HK,MO,TW)
  // 不用全等是为了防止存在半角和全角的括号混杂导致不能全等
  if (enText?.includes('Hongkong,Macao,Taiwan')) {
    enText = "China(HK,MO,TW)";
  }
  return {
    // ...item,
    // 没有传code的时候，用英文名称作为abbre和key，如：ua页面
    region_abbre: areaCode.toLowerCase() || enText,
    region_key: areaCode || enText,
    region_name_cn: changeParentheses(chText),
    // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
    region_name_en: changeParentheses(enText),
    // region_name_en: enText,
    country: (country || []).map(it => {
      const { country_code: countryCode = '', country_name_ch: chText, country_name_en: enText } = it;
      // return {
      //   ...it,
      //   chText,
      //   enText:isEn ? enText : chText,
      //   country_name_abbre: countryCode.toLowerCase(),
      // }
      // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
      return {
        ...it,
        country_name_ch: changeParentheses(chText),
        country_name_en: changeParentheses(enText),
        chText: changeParentheses(chText),
        enText: changeParentheses(enText),
        country_name_abbre: countryCode.toLowerCase(),
      };
    })
  };
});

// Category option 格式化
export const formatCategoryOptions: (options: any[]) => Array<FormatAreaItemType> = (options) => (options || []).map(item => {
  const { product_set: country, category_code: areaCode = '', category_name_ch: chText, category_name_en: enText } = item;

  return {
    // ...item,
    // 没有传code的时候，用英文名称作为abbre和key，如：ua页面
    region_abbre: areaCode.toLowerCase() || enText,
    region_key: areaCode || enText,
    region_name_cn: changeParentheses(chText),
    // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
    region_name_en: changeParentheses(enText),
    // region_name_en: enText,
    country: (country || []).map(it => {
      const { product_code: countryCode = '', product_name_ch: chText, product_name_en: enText } = it;
      // return {
      //   ...it,
      //   chText,
      //   enText:isEn ? enText : chText,
      //   country_name_abbre: countryCode.toLowerCase(),
      // }
      // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
      return {
        ...it,
        country_name_ch: changeParentheses(chText),
        country_name_en: changeParentheses(enText),
        chText: changeParentheses(chText),
        enText: changeParentheses(enText),
        country_name_abbre: countryCode.toLowerCase(),
        country_code: countryCode,
      };
    })
  };
});

// 筛选器confirm的时候判断传出的值是否和方案保存（方案中存在的筛选器）的值一样
export const saveIsEqualConfirm = (saveValue: any, confirmValue: any) => {
  // 不同页面筛选器类型和个数可能不同，如：时间对比页多了个对比时间，所以这里以方案为基准，和方案中已有的筛选器值不同的才重置
  const isNotEqualArr = Object.keys(saveValue)?.filter((item) => {
    const isDate = DATE_FILTER_ARR.includes(item);
    
    if (isDate) {
      const confirmVal = confirmValue?.[item]?.value || confirmValue?.[item];
      const saveVal = saveValue?.[item]?.value || saveValue?.[item];
      if (Array.isArray(saveVal)) {
        // 转化为日周月是因为同日期下moment中时分秒可能不同
        const confirmStartDate = getDateVal(confirmVal?.[0]);
        const confirmEndDate = getDateVal(confirmVal?.[1]);
        const saveStartDate = getDateVal(saveVal?.[0]);
        const saveEndDate = getDateVal(saveVal?.[1]);
        return confirmStartDate !== saveStartDate || confirmEndDate !== saveEndDate;
      } else {
        const filterSaveValue = getDateVal(saveVal);
        const confirmValue = getDateVal(confirmVal);
        return filterSaveValue !== confirmValue;
      }
      
    }
    return !isEqual(saveValue?.[item],confirmValue?.[item]);
  }) || [];
  return isNotEqualArr?.length > 0;
};

const handleItemDefaultValue = (item: any, key: string, urlParams: any, obj: any) => {
  if (typeof item.default === 'string') {
    obj[key] = urlParams[key];
  } else if (Array.isArray(item.default)) {
    obj[key] = urlParams[key]?.split(',');
  } else {
    console.log('新的数据格式');
  }
};

// todo 其他页面的逻辑补齐
export const getUrlParams = (urlParams: UrlParams, inputList: Array<InputItemType>) => {
  const { start, end, timeType, rangeDateKey, ...others } = urlParams || {};
  const obj: urlParamsObj = {};
  // 筛选器值url传参处理，只处理后端在页面中配置的可展示筛选器的值，后端未配置以及配置的不展示只传默认值的筛选器不做处理
  const realInpputList = inputList?.filter(item => item?.visible === 1 || (item.visible !== 0 && !item.visible));
  realInpputList.forEach(((item: InputItemType) => {
    const key: string = item.type || item.key;
    // 时间（范围）筛选器处理
    if (DATE_RANGE_ARR.includes(key) && start && end) {
      // 有些页面type 是rangeDate，key是rangeTime
      // || FILTER_VALUE_TYPE_DATE?.includes(key)
      if (key === RANGE_DATE) {
        obj[RANGE_TIME] = [start, end];
      } else {
        (obj as { [K in urlParamsObjKeys]?: any })[key as urlParamsObjKeys] = [start, end];
      }
    }
    // 其他类型筛选器处理
    if (others[key]) {
      const { [REGION]: marketList, [CATEGORY]: categoryList, [PRODUCT]: productList } = others;
      if (key === REGION) {
        // 区域筛选器单独处理
        obj[key] = {
          markets: marketList?.split(',') || [],
          region_type: REGION,
          regions: []
        };
      } else if (key === CATEGORY) {
        // category筛选器单独处理
        obj[key] = {
          product: productList?.split(',') || [],
          category_type: PRODUCT,
          category: categoryList?.split(',') || [],
        };
      } else {
        // 其他筛选器统一处理
        handleItemDefaultValue(item, key, others, obj);
      }
    }
  }));
  // 多粒度时间筛选器需要传timeType，不传则为Day
  const result = {
    filterValue: obj,
    ...(timeType ? { timeType } : {}),
    ...(rangeDateKey ? {rangeDateKey} : {})
  };
  return result;
};

export const formatRegionSingleSelectOptions = (options) => {
  if (!options || !Array.isArray(options)) {
    return [];
  }

  const formattedCountries = [];

  options.forEach(area => {
    const { area_code, area_name_ch, area_name_en, country_set } = area;
    // 过滤掉 area_code 为 "GLOBAL" 的区域
    if (area_code === "GLOBAL") {
      return;
    }
    
    if (country_set && Array.isArray(country_set)) {
      country_set.forEach(country => {
        // 检查是否是 "255" 的特殊情况
        if (country.country_code === "255") {
          formattedCountries.push({
            code: "global",
            name_ch: "全部",
            name_en: "All",
            area_code: "global",
            area_name_ch: "全部",
            area_name_en: "All",
            info: "All表示当前对应国家列表的全部数据汇总。"
          });
        } else {
          formattedCountries.push({
            code: country.country_code,
            name_ch: country.country_name_ch,
            name_en: country.country_name_en,
            area_code,
            area_name_ch,
            area_name_en,
            icon: getCountryFlag(country.country_code),
          });
        }
      });
    }
  });

  return formattedCountries;
};

/**
 * 判断是否是新级联筛选器（排除 region 和 category）
 * 新级联筛选器是指 view_type 为 'cascade' 或 'singleCascade'，且不是 region 和 category 的筛选器
 * @param item - 筛选器配置项
 * @param key - 筛选器的 key（可选，如果不传则从 item 中获取）
 * @returns 是否是新级联筛选器
 */
export const isNewCascadeFilter = (item: any, key?: string): boolean => {
  if (!item) {
    return false;
  }
  const filterKey = key || item?.key || item?.type;
  if (!filterKey) {
    return false;
  }
  return (item?.view_type === 'cascade' || item?.view_type === 'singleCascade')
    && filterKey !== REGION
    && filterKey !== CATEGORY;
};

/**
 * 对 inputList 中的级联筛选器进行字段映射
 * 将后端字段（snake_case）映射为前端字段（camelCase），并添加到 item 对象上
 * @param inputList - 筛选器配置列表
 * @param locale - 语言环境
 * @returns 处理后的筛选器配置列表
 */
export const mapCascadeFieldsToInputList = (inputList: Array<InputItemType>, locale: string): Array<InputItemType> => {
  if (!inputList || inputList.length === 0) {
    return inputList;
  }
  
  const isEn = locale === LANGUAGE_EN;
  
  return inputList.map((item: any) => {
    const key: string = item.type || item.key;
    
    // 判断是否是新级联筛选器
    const isNewCascade = isNewCascadeFilter(item, key);
    
    // 如果不是新级联筛选器，直接返回
    if (!isNewCascade) {
      return item;
    }
    
    // 获取筛选器名称（用于映射）
    const name = isEn ? item.name_en : item.name_cn;
    
    // 进行字段映射
    const cascadeFields = mapCascadeFields(item, locale, isNewCascade, key, name);
    
    // 将映射后的字段添加到 item 对象上
    return {
      ...item,
      lftName: cascadeFields.lftName,
      rtName: cascadeFields.rtName,
      parentChildName: cascadeFields.parentChildName,
      parentKey: cascadeFields.parentKey,
      childKey: cascadeFields.childKey,
      radioOptions: cascadeFields.radioOptions,
      allInfo: cascadeFields.allInfo || (isEn ? item.all_info_en : item.all_info_cn),
      ...(cascadeFields.childPanelWidth ? { childPanelWidth: cascadeFields.childPanelWidth } : {}),
      ...(cascadeFields.optionDisplayOrder
        ? { optionDisplayOrder: cascadeFields.optionDisplayOrder }
        : {}),
      ...(cascadeFields.tooltip ? { tooltip: cascadeFields.tooltip } : {}),
    };
  });
};

/**
 * 构建 system 筛选器 cascade 模式的 props
 *
 * 处理逻辑：
 * 1. 选项映射：将后端返回的 system 选项字段（os_id / os_name_ch / os_name_en）
 *    映射为 CascadeFilterComponent 期望的通用字段（code / name_ch / name_en）
 * 2. 值格式转换（入方向）：将外部传入的数组格式 value（如 ['255']）
 *    转换为 cascade 组件期望的对象格式（如 { platform_type: [], system: ['255'], type: 'system' }）
 * 3. onChange 包装（出方向）：将 cascade 组件输出的对象格式
 *    转换回外部期望的子项数组格式（如 ['epic', 'gog', 'steam']），
 *    始终提取 childKey 维度的值，确保外部不会收到父类数组
 *
 * @param props - renderSystem 接收到的完整 props
 * @param props.key - 筛选器 key（如 'system'）
 * @param props.value - 当前筛选器的值，可能是数组或 cascade 对象
 * @param props.options - 后端返回的选项列表，含 os_id / os_name_ch / os_name_en 字段
 * @param props.onChange - 外部传入的 onChange 回调
 * @param props.parentKey - 级联筛选器父级 key（来自 fieldMapper 映射，如 'platform_type'）
 * @param props.childKey - 级联筛选器子级 key（来自 fieldMapper 映射，如 'system'）
 * @returns 转换后的 cascade props 对象，包含 cascadeOptions / cascadeValue / cascadeOnChange
 */
export const buildSystemCascadeProps = (props: {
  key: string;
  value: any;
  options: any[];
  onChange: (filterKey: string, val: any) => void;
  parentKey?: string;
  childKey?: string;
}) => {
  const { key, value, options, onChange, parentKey, childKey } = props;

  // 1. 选项字段映射：os_id -> code, os_name_ch -> name_ch, os_name_en -> name_en
  // system 级联中，255 表示“全部”，不应出现在左侧 Type 分组里
  const cascadeOptions = options
    ?.filter((item: any) => String(item?.os_id) !== '255')
    .map((item: any) => ({
      ...item,
      code: item.os_id,
      name_ch: item.os_name_ch,
      name_en: item.os_name_en,
    }));

  // 使用映射后的 parentKey/childKey（来自 fieldMapper），兜底用 'parent'/key
  const pKey = parentKey || 'parent';
  const cKey = childKey || key;

  // 2. 值格式转换（入方向）：数组 -> cascade 对象
  // 数组格式 ['255'] -> { platform_type: [], system: ['255'], type: 'system' }
  const cascadeValue = Array.isArray(value)
    ? { [pKey]: [], [cKey]: value, type: cKey }
    : value;

  // 3. onChange 包装（出方向）：cascade 对象 -> 子项数组
  // 始终提取 childKey（子项）的值，因为外部期望的是子项数组（如 ['epic','gog','steam']）
  // 不使用 val[val.type] 是因为当勾选整个父类时 type 会变成 parentKey，
  // 此时 val[val.type] 会提取到父类数组而不是子项数组
  const cascadeOnChange = (filterKey: string, val: any) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const activeValues = val[cKey] || [];
      onChange(filterKey, activeValues);
    } else {
      onChange(filterKey, val);
    }
  };

  return {
    cascadeOptions,
    cascadeValue,
    cascadeOnChange,
  };
};